<script setup lang="ts">
const route = useRoute()
const colorMode = useColorMode()
const isMobileMenuOpen = ref(false)

const isDark = computed({
  get: () => colorMode.value === 'dark',
  set: (v) => { colorMode.preference = v ? 'dark' : 'light' },
})

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Projects', to: '/projects' },
  { label: 'Lab', to: '/lab' },
  { label: 'Blog', to: '/blog' },
  { label: 'Uses', to: '/uses' },
  { label: 'Contact', to: '/contact' },
]

watch(() => route.path, () => {
  isMobileMenuOpen.value = false
})
</script>

<template>
  <header class="fixed top-0 left-0 right-0 z-50 border-b border-[var(--color-border)] bg-[var(--color-surface)]/80 backdrop-blur-xl">
    <nav class="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
      <NuxtLink to="/" class="flex items-center">
        <img src="/logo_light_mode.png" alt="shakir.sd" class="h-8 dark:hidden">
        <img src="/logo_dark_mode.png" alt="shakir.sd" class="hidden h-8 dark:block">
      </NuxtLink>

      <!-- Desktop nav -->
      <div class="hidden items-center gap-1 md:flex">
        <NuxtLink
          v-for="link in navLinks"
          :key="link.to"
          :to="link.to"
          class="rounded-lg px-3 py-2 text-sm font-medium text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text)]"
          :class="{ 'text-[#b8751a]! font-bold! dark:text-amber!': route.path === link.to || (link.to !== '/' && route.path.startsWith(link.to)) }"
        >
          {{ link.label }}
        </NuxtLink>
        <!-- theme is client state (localStorage): SSR can't know the icon -->
        <ClientOnly>
          <UButton
            variant="ghost"
            color="neutral"
            size="sm"
            class="ml-2"
            :icon="isDark ? 'i-lucide-sun' : 'i-lucide-moon'"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="isDark = !isDark"
          />
          <template #fallback>
            <UButton
              variant="ghost"
              color="neutral"
              size="sm"
              class="ml-2"
              icon="i-lucide-sun-moon"
              aria-label="Toggle color mode"
            />
          </template>
        </ClientOnly>
      </div>

      <!-- Mobile menu button -->
      <UButton
        class="md:hidden"
        variant="ghost"
        color="neutral"
        :icon="isMobileMenuOpen ? 'i-lucide-x' : 'i-lucide-menu'"
        :aria-label="isMobileMenuOpen ? 'Close menu' : 'Open menu'"
        size="lg"
        @click="isMobileMenuOpen = !isMobileMenuOpen"
      />
    </nav>

    <!-- Mobile nav -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="isMobileMenuOpen"
        class="border-t border-[var(--color-border)] bg-[var(--color-surface)]/95 backdrop-blur-xl md:hidden"
      >
        <div class="space-y-1 px-4 py-3">
          <NuxtLink
            v-for="link in navLinks"
            :key="link.to"
            :to="link.to"
            class="block rounded-lg px-3 py-2 text-sm font-medium text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text)]"
            :class="{ 'text-[#b8751a]! font-bold! dark:text-amber!': route.path === link.to || (link.to !== '/' && route.path.startsWith(link.to)) }"
          >
            {{ link.label }}
          </NuxtLink>
          <UButton
            variant="ghost"
            color="neutral"
            size="sm"
            class="mt-2 w-full justify-start"
            :icon="isDark ? 'i-lucide-sun' : 'i-lucide-moon'"
            @click="isDark = !isDark"
          >
            {{ isDark ? 'Light mode' : 'Dark mode' }}
          </UButton>
        </div>
      </div>
    </Transition>
  </header>
</template>
